import * as yup from "yup";
import PasswordStrength from "@/components/auth/PasswordStrength.tsx";
import Form from "@/components/ui/forms/Form.tsx";
import { useNewPasswordBetaMutation } from "@/data/user.ts";
import { NewPasswordFormInput, VerifyPasswordTokenUserInputResponse } from "@/types";
import OutlinedPasswordInput from "@edusogno/inputs/outlined-password-input.js";
import AuthBar from "@edusogno/layouts/auth-bar.js";
import AuthPageFooter from "@edusogno/layouts/auth-page-footer.js";
import AuthPageHeading from "@edusogno/layouts/auth-page-heading.js";
import { cn } from "@edusogno/lib/utils";
import LoadingButton from "@edusogno/primitives/loading-button";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import RequestClient from "@/data/client";
import ROUTES from "@/config/routes";

export default function NewPasswordForm() {
  const location = useLocation();
  const navigate = useNavigate();


  // Extract the token and email from the URL
  // Format: /user/reset-password/{token}/{encryptedEmail}
  const urlParts = location.pathname.split("/");
  const token = urlParts[urlParts.length - 1];

  const { t } = useTranslation();

  const newPasswordValidationSchema = yup.object().shape({
    password: yup
      .string()
      .matches(/(?=.*?[A-Z])/, t("auth.errors.password_upper"))
      .matches(/(?=.*?[a-z])/, t("auth.errors.password_lower"))
      .matches(/(?=.*?\d)/, t("auth.errors.password_number"))
      .matches(
        /(?=.*?[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?])/,
        t("auth.errors.password_special"),
      )
      .min(8, t("auth.errors.password_min"))
      .required(t("auth.errors.password_required")),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password")], t("auth.errors.password_match"))
      .required(t("auth.errors.password_confirm")),
  });

  const {
    getFieldState,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<NewPasswordFormInput>({
    mode: "onChange",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(newPasswordValidationSchema),
  });

  const passwordFieldState = getFieldState("password");
  const confirmPasswordFieldState = getFieldState("confirmPassword");
  const hasPasswordErrors =
    passwordFieldState.isTouched && passwordFieldState?.invalid;
  const hasConfirmPasswordErrors =
    confirmPasswordFieldState.isTouched && confirmPasswordFieldState?.invalid;

  useEffect(() => {
    if (
      (errors.password && hasPasswordErrors) ||
      (errors.confirmPassword && hasConfirmPasswordErrors)
    ) {
      enqueueSnackbar(
        errors?.password?.message ?? errors.confirmPassword?.message,
        {
          variant: "error",
        },
      );
    }
  }, [
    passwordFieldState,
    errors,
    getFieldState,
    hasPasswordErrors,
    hasConfirmPasswordErrors,
  ]);

  useEffect(() => {
    if (token) {
      RequestClient.users.verifyPasswordToken(token)
        .then((response: VerifyPasswordTokenUserInputResponse) => {
          if (response.status !== 200) {
            enqueueSnackbar("The token is invalid or has expired", {
              variant: "error",
            });
            navigate(ROUTES.password_expiration);
          }
        })
        .catch(() => {
          enqueueSnackbar("An error occurred while verifying the token", {
            variant: "error",
          });
          navigate(ROUTES.password_expiration); 
        });
    } else {
      navigate(ROUTES.password_expiration);
    }
  }, [token, navigate]);

  const { mutate, isLoading } = useNewPasswordBetaMutation();

  const onSubmit: SubmitHandler<NewPasswordFormInput> = (data) => {
    mutate({
      password: data.password,
      token: token,
    });
  };


  

  return (
    <Form<NewPasswordFormInput>
      onSubmit={
        handleSubmit(onSubmit) as unknown as SubmitHandler<NewPasswordFormInput>
      }
    >
      {() => (
        <>
          <AuthBar progress={0} onClick={() => {}} />

          <div
            className={
              "flex flex-col flex-[4_0_40%]  lg:max-w-[55%] lg:min-w-[500px]  pt-6 2xl:pt-8  mx-auto gap-3  px-5 md:gap-4  w-full items-center "
            }
          >
            <AuthPageHeading
              title={t("auth.new.text")}
              subtitle={t("auth.new.subtext")}
            />
            <div
              className={cn(
                "flex flex-col flex-[1_1_100%]  3xl:pt-12 w-full gap-4 h-full",
              )}
            >
              <OutlinedPasswordInput
                label={t("auth.labels.new_password")}
                placeholder={"********"}
                autoComplete={"off"}
                {...register("password")}
                error={Boolean(errors.password?.message)}
              />
              <OutlinedPasswordInput
                label={t("auth.labels.confirm_password")}
                placeholder={"********"}
                autoComplete={"off"}
                {...register("confirmPassword")}
                error={Boolean(errors.confirmPassword?.message)}
              />

              <PasswordStrength />
            </div>
          </div>

          <AuthPageFooter>
            <div
              className={
                "font-normal text-lg flex flex-row items-center justify-center md:text-lg  pt-4 mb-3 text-center text-brand w-full px-1"
              }
            >
              <div
                className={
                  "w-full max-w-md mx-auto flex flex-col items-center px-6 pt-8 pb-2  mb-4"
                }
              >
                <LoadingButton
                  size={"lg"}
                  type="submit"
                  className="w-full text-lg tracking-wide hidden md:block"
                  loading={isLoading}
                  disabled={
                    isLoading || hasPasswordErrors || hasConfirmPasswordErrors
                  }
                >
                  {t("auth.reset.reset")}
                </LoadingButton>
                <LoadingButton
                  size={"md"}
                  type="submit"
                  className="w-full text-lg tracking-wide block md:hidden"
                  loading={isLoading}
                  disabled={
                    isLoading || hasPasswordErrors || hasConfirmPasswordErrors
                  }
                >
                  {t("auth.reset.reset")}
                </LoadingButton>
              </div>
            </div>
          </AuthPageFooter>
        </>
      )}
    </Form>
  );
}
